"use client";

import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@clerk/nextjs";
import { useUserLGSs } from "modules/lgs/hooks/useUserLGSs";
import {
  reverseGeocode,
  ReverseGeocodeResult,
} from "modules/server/fetch/geocoding";
import { createCityUrl } from "modules/common/components/map/utils/formatCityName";
import { STATIC_DATA_CONFIG } from "modules/common/react-query-configs";

/**
 * Resolves the viewer's `/city/{slug}/matched` route from their joined
 * stores. Returns null while resolving, when the viewer's stores span
 * multiple cities (ambiguous — a city-match notification doesn't say which
 * city it happened in), or when no city can be resolved. Callers fall back
 * to the pairwise `/users/{username}/matched` page, which includes both store
 * and city scopes for the notification's counterparty.
 */
export function useViewerCityMatchedRoute(enabled: boolean): string | null {
  const { userId } = useAuth();
  const { userLgs } = useUserLGSs(enabled ? (userId ?? "") : "");

  const { data } = useQuery({
    queryKey: [
      "viewer-city-matched-route",
      userLgs
        .map((store) => store.wotcId)
        .sort()
        .join(","),
    ],
    queryFn: async () => {
      const results = await Promise.all(
        userLgs.map((store) => reverseGeocode(store.latitude, store.longitude))
      );
      const cityUrls = new Set(
        results
          .filter((result): result is ReverseGeocodeResult => result !== null)
          .map((result) => createCityUrl(result.country, result.city))
      );
      if (cityUrls.size !== 1) return null;
      return `${[...cityUrls][0]}/matched`;
    },
    enabled: enabled && userLgs.length > 0,
    ...STATIC_DATA_CONFIG,
  });

  return data ?? null;
}
